import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import { AgGridReact } from '@ag-grid-community/react';
import { Grid } from '@mui/material';
import { useAgGrid } from '@tassat/ag-grid';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { ApiConnectionUserMapping } from '@tassat/fred-extended-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getApiConnectionUsersandFirm } from 'Selectors/apiConnectionUsers';
import { ActionButtonsCellRenderer } from 'SharedComponents/cellRenderComponent/actionButtonsCellRenderer';

import { ApiConnectionUsersColumnDefs } from './apiUsersGridConfig';

import type { IAPIUsersAgGridProps } from '.';

import './apiUserViewStyles.css';

export function APIConnectionUsersAgGrid(props: IAPIUsersAgGridProps) {
    const { apiConnectionID } = props;
    const usersFromApiConnection = useAppSelector((state) => getApiConnectionUsersandFirm(state, '', apiConnectionID));

    const [ ,onGridReady ] = useAgGrid();

    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);

    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const deleteApiUser = async (apiConnectionUser: ApiConnectionUserMapping) => {
        const id = apiConnectionUser.apiUser.APIConnectionUserId;
        const user = apiConnectionUser.user;
        const apiConnection = apiConnectionUser.apiConnection;

        try {
            const response = await Worker.getInstance()
                .deleteAPIConnectionUser(id);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Deleted Connection for ${user.Username}`,
                        successBody: `Successfully deleted the connection for ${user.Username} to ${apiConnection?.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete Connection for ${user.Username}`,
                        failureBody: `Could not delete the connection for ${user.Username} to ${apiConnection?.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Delete Connection for ${user.Username}`,
                    failureBody: `Something went wrong deleting the connection for ${user.Username} to ${apiConnection?.APIConnectionName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const deleteApiUserValidation = (apiConnectionUser: ApiConnectionUserMapping): boolean => {
        const user = apiConnectionUser.user;
        const apiConnection = apiConnectionUser.apiConnection;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiConnectionUser,
                actionType: ActionTypes.delete,
                confirmTitle: `Confirm Deleting Connection for ${user.Username}`,
                confirmBody: `Please confirm deleting the connection for ${user.Username} to ${apiConnection.APIConnectionName}`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockApiUserValidation = (apiConnectionUser: ApiConnectionUserMapping): boolean => {
        const apiUser = apiConnectionUser.apiUser;
        const user = apiConnectionUser.user;
        const apiConnection = apiConnectionUser.apiConnection;

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiConnectionUser,
                actionType: apiUser.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${apiUser.IsLocked ? 'Unlocking' : 'Locking'} Connection for ${user.Username}`,
                confirmBody: `Please confirm ${apiUser.IsLocked ? 'unlocking' : 'locking'} the connection for ${user.Username} to ${apiConnection?.APIConnectionName}`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockApiUser = async (apiConnectionUser: ApiConnectionUserMapping) => {
        const apiUser = apiConnectionUser.apiUser;
        const user = apiConnectionUser.user;
        const apiConnection = apiConnectionUser.apiConnection;

        try {
            const response = await Worker.getInstance()
                .updateAPIConnectionUser({
                    APIConnectionUserId: apiUser.APIConnectionUserId,
                    IsLocked: !apiUser.IsLocked
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${apiUser.IsLocked ? 'Unlocked' : 'Locked'} Connection for ${user.Username}`,
                        successBody: `Successfully ${apiUser.IsLocked ? 'unlocked' : 'locked'} the connection for ${user.Username} to ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${apiUser.IsLocked ? 'Unlock' : 'Lock'} Connection for ${user.Username}`,
                        failureBody: `Could not ${apiUser.IsLocked ? 'unlock' : 'lock'} the connection for ${user.Username} to ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${apiUser.IsLocked ? 'Unlock' : 'Lock'} Connection for ${user.Username}`,
                    failureBody: `Something went wrong ${apiUser.IsLocked ? 'unlocking' : 'locking'} the connection for ${user.Username} to ${apiConnection.APIConnectionName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                preActionValidationAndConfig: {
                    [ActionTypes.delete]: deleteApiUserValidation,
                    [ActionTypes.lock]: lockApiUserValidation,
                    [ActionTypes.unlock]: lockApiUserValidation
                },
                onConfirm: {
                    [ActionTypes.delete]: deleteApiUser,
                    [ActionTypes.lock]: lockApiUser,
                    [ActionTypes.unlock]: lockApiUser
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                container
                item
                justifyContent='center'
            >
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='center'
                    xs={12}
                >
                    <div
                        className='ag-theme-material'
                        id='apiUsers'
                        style={{
                            width: '100%',
                            height: 500
                        }}
                    >
                        <AgGridReact
                            animateRows={false}
                            columnDefs={ApiConnectionUsersColumnDefs}
                            columnMenu='legacy'
                            components={{ actionButtonsCellRenderer: ActionButtonsCellRenderer }}
                            defaultColDef={{
                                resizable: true,
                                filter: true,
                                sortable: true
                            }}
                            enableCellTextSelection={true}
                            getRowId={({ data }) => data.apiUser.APIConnectionUserId}
                            onGridReady={onGridReady}
                            rowClassRules={{ 'lockedRow': (params) => params.data.apiUser.IsLocked }}
                            rowData={usersFromApiConnection}
                            suppressCellFocus={true}
                            suppressContextMenu={true}
                        />
                    </div>
                </Grid>
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}